import { useEffect, useRef, useState } from 'react';
import { EmergencyHotlinePanel } from './EmergencyHotlinePanel';
import { ResetConfirmModal } from './ResetConfirmModal';
import { ResponseCard } from './ResponseCard';
import { Section109Guard } from './Section109Guard';
import { UsageBadge } from './UsageBadge';
import { useChat } from './useChat';
import { useEmergencyMode } from './useEmergencyMode';
import styles from './ChatConversation.module.css';

const WELCOME_TEXT_EMERGENCY =
  '야간 응급 상담 모드입니다. 지금 겪고 계신 상황을 알려주세요 — 응급 사안으로 분류되면 변호사 즉시 콜백으로 연결됩니다.';
const WELCOME_TEXT_DAYTIME =
  '안녕하세요, 메디로이어입니다. 의료 현장에서 발생한 법률 이슈를 상담해드립니다. 상황을 자세히 알려주세요.';

export function ChatConversation({ variant = 'modal' }) {
  const timeEmergency = useEmergencyMode();
  const {
    session,
    messages,
    remaining,
    nextAction,
    sending,
    error,
    adminTyping,
    closed,
    send,
    notifyTyping,
    submitHotline,
    closeConsultation,
    reset,
  } = useChat();
  const [input, setInput] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const scrollRef = useRef(null);

  // 새 메시지마다 스크롤 하단으로
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const hotlineMode = nextAction === 'collect_phone_for_hotline';
  // 핫라인 신청 완료 후에도 같은 세션에서 다른 사안을 묻을 수 있도록 입력창 다시 열어둠.
  // (기획 02 §8 의 E2 — 인계 후에도 다른 일반 질문 가능)
  const exhausted = nextAction === 'quota_exhausted';
  const inputDisabled = sending || !session || hotlineMode || exhausted || closed;

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || inputDisabled) return;
    setInput('');
    send(text);
  };

  return (
    <div
      className={`${styles.root} ${styles[variant] || ''} ${
        timeEmergency ? styles.emergency : styles.daytime
      }`}
    >
      <Section109Guard />

      <div className={`${styles.modeBanner} ${timeEmergency ? '' : styles.modeBannerDay}`}>
        <span className={styles.modeBannerLeft}>
          <span
            className={`${styles.modeDot} ${timeEmergency ? '' : styles.modeDotOnline}`}
            aria-hidden="true"
          />
          {timeEmergency
            ? '야간 응급 상담 모드 · 변호사 직접 호출'
            : '담당자 상담 중 · 평일 09–21시'}
          <UsageBadge remaining={remaining} daytimeMode={session?.daytime_mode} />
        </span>
        <button
          type="button"
          className={styles.endBtn}
          onClick={() => setCloseOpen(true)}
          disabled={sending || !session || closed || messages.length === 0}
          aria-label="상담 종료"
        >
          상담 종료
        </button>
        <button
          type="button"
          className={styles.resetBtn}
          onClick={() => setResetOpen(true)}
          disabled={sending || !session || messages.length === 0}
          aria-label="새 대화 시작"
          title="새 대화 시작"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M13.5 8a5.5 5.5 0 1 1-1.6-3.88M13.5 3v3h-3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className={styles.messages} ref={scrollRef}>
        <div className={styles.dateSep}>오늘</div>

        {messages.length === 0 && (
          <MessageRow from="bot">
            {timeEmergency ? WELCOME_TEXT_EMERGENCY : WELCOME_TEXT_DAYTIME}
          </MessageRow>
        )}

        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}

        {/* 담당자 타이핑 중 표시 — sending 상태가 아닐 때만 노출 */}
        {adminTyping && !sending && (
          <MessageRow from="bot">
            <span className={styles.typing}>담당자가 입력 중…</span>
          </MessageRow>
        )}

        {sending && (
          <MessageRow from="bot">
            <span className={styles.typing}>응답을 생성 중입니다</span>
          </MessageRow>
        )}

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        {hotlineMode && <EmergencyHotlinePanel onSubmit={submitHotline} submitting={sending} />}

        {exhausted && (
          <div className={styles.exhausted}>
            <p>이번 달 무료 응답 한도를 모두 사용하셨습니다.</p>
            <button type="button" className={styles.consultBtn}>
              변호사 상담 신청
            </button>
          </div>
        )}
        {closed && (
          <div className={styles.closedPanel}>
            <p>상담이 종료되었습니다. 이용해 주셔서 감사합니다.</p>
            <button type="button" className={styles.consultBtn} onClick={reset}>
              새 상담 시작
            </button>
          </div>
        )}
      </div>

      {!hotlineMode && !exhausted && !closed && (
        <form className={styles.inputBar} onSubmit={handleSubmit}>
          <input
            type="text"
            className={styles.input}
            placeholder={session ? '메시지를 입력하세요' : '연결 중...'}
            aria-label="메시지 입력"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              notifyTyping();
            }}
            disabled={inputDisabled}
            maxLength={5000}
          />
          <button
            type="submit"
            disabled={inputDisabled || !input.trim()}
            className={styles.sendBtn}
            aria-label="전송"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M3 12l18-9-6 18-3.5-7.5L3 12z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </form>
      )}

      <div className={styles.footerNote}>
        법무법인 오킴스 · 메디로이어 · 의료인 전용 응급 상담 서비스
      </div>

      <ResetConfirmModal
        open={resetOpen}
        onCancel={() => setResetOpen(false)}
        onConfirm={() => {
          setResetOpen(false);
          reset();
        }}
      />
      <ResetConfirmModal
        open={closeOpen}
        onCancel={() => setCloseOpen(false)}
        onConfirm={() => {
          setCloseOpen(false);
          closeConsultation();
        }}
        title="상담을 종료할까요?"
        body="종료 후에는 이 대화를 이어갈 수 없습니다. (변호사가 검토할 수 있도록 대화 기록은 보관됩니다.)"
        confirmLabel="상담 종료"
      />
    </div>
  );
}

function MessageBubble({ message }) {
  if (message.role === 'user') {
    return <MessageRow from="user">{message.content}</MessageRow>;
  }
  const s = message.structured;

  if (s?.intent === 'response_card' && s.card) {
    return (
      <MessageRow from="bot" wide>
        <ResponseCard card={s.card} precedents={s.precedents || []} />
      </MessageRow>
    );
  }

  if (s?.intent === 'clarification' && Array.isArray(s.questions) && s.questions.length > 0) {
    return (
      <MessageRow from="bot">
        <div>
          상황을 좀 더 자세히 알려주세요:
          <ul className={styles.clarifyList}>
            {s.questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </div>
      </MessageRow>
    );
  }

  // hotline_prompt / hotline_accepted / quota_exhausted / card_pending 등 — 텍스트 그대로
  return <MessageRow from="bot">{message.content}</MessageRow>;
}

function MessageRow({ from, wide, children }) {
  return (
    <div
      className={`${styles.msg} ${from === 'bot' ? styles.bot : styles.user}`}
      style={wide ? { maxWidth: '100%' } : undefined}
    >
      {from === 'bot' && (
        <div className={styles.msgAvatar} aria-hidden="true">
          M
        </div>
      )}
      <div className={styles.bubble} style={wide ? { width: '100%' } : undefined}>
        {children}
      </div>
    </div>
  );
}
